import ErrorDisplay from '../../../components/ErrorDisplay';
import LessonsView from './LessonsView';
import Loading from '../../../components/Loading';
import { Box, Heading, Separator, Stack } from '@chakra-ui/react';
import { Client, Lesson, LessonStudent } from '@opanuj/shared/types';
import { EnrollmentList } from '../../enrollment/EnrollmentList';
import { formatLocation, formatWeekday } from '@opanuj/shared/utils';
import { setDocumentTitle } from '../../../utils/documentTitle';
import { useEffect } from 'react';
import { useLessonsList } from '../../../hooks/useLessons';
import { useReservationsList } from '../../../hooks/useReservations';
import { useSlotsList } from '../../../hooks/useSlots';
import { useUser } from '../../../hooks/useUser';

export interface LessonWithClient extends Lesson {
	students: (LessonStudent & {
		client: Client;
	})[];
}

const LessonsController = () => {
	useEffect(setDocumentTitle('Lekcje')); // eslint-disable-line react-hooks/exhaustive-deps
	
	const [user, userLoading, userError] = useUser();
	const [lessons, loadingLessons, lessonsError] = useLessonsList();
	const [reservations, reservationsLoading, reservationsError] = useReservationsList();
	const [slots, slotsLoading, slotsError] = useSlotsList();
	
	if (userLoading || loadingLessons || reservationsLoading || slotsLoading) return <Loading/>;
	if (userError || lessonsError || reservationsError || slotsError) {
		return <ErrorDisplay error={userError || lessonsError || reservationsError || slotsError}/>;
	}
	
	const userLessons = lessons!.filter(({ students }) => students.map(({ uid }) => uid).includes(user!.uid));
	const reservationLessons = reservations!
		.filter((reservation) => reservation.uid === user!.uid)
		.flatMap((reservation) => {
			return (reservation.lessons ?? []).map((lesson) => {
				const slot = slots.find((item) => item.id === lesson.slot);
				if (!slot) return null;
				return {
					code: slot.code,
					dayFull: formatWeekday(slot.day),
					time: slot.time,
					duration: slot.duration,
					locationFull: formatLocation(slot.location),
					teacher: slot.defaultTeacher,
					student: { name: lesson.student?.name ?? 'Brak uczestnika' },
					notes: lesson.notes ?? '',
				};
			}).filter(Boolean);
		});

	if (!userLessons.length && !reservationLessons.length) {
		return <LessonsView lessons={[]} />;
	}

	return (
		<Stack gap={4} mx={{ base: 2, md: 6, lg: 10 }} my={4} align="center">
			{reservationLessons.length > 0 && (
				<>
					<Box
						w="full"
						maxW="900px"
						p={4}
						mx="auto"
					>
						<Heading size="md" fontWeight='semibold' my={5} textAlign="center" color="gray.600" letterSpacing='wider'>
							Twoje rezerwacje oczekujące na potwierdzenie
						</Heading>
						<EnrollmentList lessonList={reservationLessons} actionType="none" />
					</Box>
					<Separator w="full" my={8}/>
				</>
			)}
			<LessonsView lessons={userLessons} />
		</Stack>
	);
}
export default LessonsController;